import { v4 as uuid } from 'uuid';
import { computeUnitPrice } from '@/features/menu/computeUnitPrice';
import type { CartLine, Menu, SelectedOption } from '@/types';
import type { TakenOverItem } from './waiterApi';

function eurosToCents(value: string): number {
  return Math.round((parseFloat(value) || 0) * 100);
}

/**
 * SC-953 — the dishes of a round a waiter took over (« Modifier »), rebuilt as
 * lines of that waiter's cart, exactly as if they had been tapped on the
 * Plats tab: real option groups (so the options sheet opens them pre-filled),
 * dine-in price, menu photo.
 *
 * Nothing is ever dropped. By the time this runs sale_api has already taken
 * the round out of the queue, so a dish or an option the menu no longer
 * carries still becomes a line — server name and price, options under a group
 * of their own with their names kept — and the cart's stale-line check
 * (`diagnoseWaiterCart`) names it before anything is sent.
 */
export function takenOverItemsToCartLines(
  items: TakenOverItem[],
  menu: Menu | undefined,
  isBuffetDineIn: boolean,
): CartLine[] {
  const productsById = new Map((menu?.items ?? []).map((product) => [product.id, product]));

  return items.map((item) => {
    const product = productsById.get(item.productUuid);
    const groups = product?.optionGroups ?? [];
    const byGroup = new Map<string, SelectedOption['options']>();
    const unresolvedNames: string[] = [];

    for (const option of item.options) {
      const id = option.option_value_uuid;
      if (!id) continue;
      const qty = Math.max(1, Number(option.qty) || 1);
      const group = groups.find((g) => g.options.some((o) => o.id === id));
      const groupId = group?.id ?? '';
      if (!group) unresolvedNames.push(qty > 1 ? `${qty}× ${option.name ?? ''}` : (option.name ?? ''));
      byGroup.set(groupId, [...(byGroup.get(groupId) ?? []), { id, qty }]);
    }

    const selectedOptions: SelectedOption[] = [...byGroup].map(([groupId, options]) => ({ groupId, options }));

    return {
      lineId: uuid(),
      itemId: item.productUuid,
      qty: item.qte,
      selectedOptions,
      unitPrice: product
        ? computeUnitPrice(product, groups, selectedOptions, isBuffetDineIn, 'dine_in')
        : eurosToCents(item.unitTtc),
      note: item.comment || undefined,
      snapshot: product
        ? { name: product.name, photo: product.photo, code: product.code }
        : { name: item.name, photo: '', code: item.code || undefined },
      optionNames: unresolvedNames.length > 0 ? unresolvedNames : undefined,
      // DF-0224 — ces plats sont ceux du client, sortis de la file par la
      // reprise : le panier les met à l'abri au lieu de les laisser mourir
      // avec l'onglet.
      takenOver: true,
    };
  });
}
